import { coerceBooleanProperty } from '@angular/cdk/coercion';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { KeyboardModule } from '@qtek/libs/keyboard';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { getLocalIsoString } from '@qtek/shared/utils';
import moment from 'moment';

import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { ErrorComponent } from '../error/error.component';
import { TimePickerComponent } from '../time-picker/time-picker.component';

@Component({
  selector: 'qt-date-hour-picker',
  templateUrl: './date-hour-picker.component.html',
  styleUrls: ['./date-hour-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateHourPickerComponent),
      multi: true,
    },
  ],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    ErrorComponent,
    TimePickerComponent,
    TranslationCoreModule,
    KeyboardModule,
  ],
})
export class DateHourPickerComponent
  implements OnInit, ControlValueAccessor, AfterViewInit, OnDestroy
{
  private inj = inject(Injector);
  private cdr = inject(ChangeDetectorRef);

  @Input() placeholder: string;
  @Input({ transform: coerceBooleanProperty }) time: boolean;
  @Input({ transform: coerceBooleanProperty }) requiredDate: boolean;
  @Input({ transform: coerceBooleanProperty }) requiredTime: boolean;
  @Input({ transform: coerceBooleanProperty }) disabled: boolean;
  @Input() openOnFocus = true;
  @Input() responsive = true;
  @Input() label: string;
  @Input() timezone = 'Etc/UCT';
  @Input() outputFormat: string;

  @Input()
  set qtDisabled(value: boolean) {
    this.disabled = value;
    if (this.fullTime && this.ts) {
      if (value) {
        this.ts.disable({ emitEvent: false });
        this.fullTime.disable({ emitEvent: false });
      } else {
        this.ts.enable({ emitEvent: false });
        this.fullTime.enable({ emitEvent: false });
      }
    }
  }

  hostControl: NgControl;
  fullTime: UntypedFormControl;
  ts: UntypedFormControl;
  private lastValid: moment.Moment | null = null;
  // tslint:disable no-empty
  propagateChange: any;
  // tslint:enable no-empty
  onTouched: any;
  destroy$ = new Subject<void>();

  ngOnInit() {
    this.ts = new UntypedFormControl({ value: '', disabled: this.disabled });
    this.fullTime = new UntypedFormControl({ hour: '00', minutes: '00' });

    if (this.requiredDate) {
      this.ts.setValidators(Validators.required);
      this.fullTime.setValidators(Validators.required);
    }
    this.fullTime.valueChanges
      .pipe(
        filter(() => this.ts.valid && this.fullTime.valid),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.ts.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      });

    this.ts.valueChanges
      .pipe(
        filter(() => this.ts.valid && this.fullTime.valid),
        takeUntil(this.destroy$)
      )
      .subscribe((value: moment.Moment | Date | string) => {
        const date = this.toMoment(value);
        if (!date || !date.isValid()) {
          this.propagateChange(null);
          return;
        }

        if (!this.fullTime.value) {
          this.fullTime.setValue(
            { hour: '00', minutes: '00' },
            { emitEvent: false }
          );
        }

        const h = +this.fullTime.value.hour;
        const m = +this.fullTime.value.minutes;
        date.set('hours', h);
        date.set('minutes', m);

        this.lastValid = date.clone();

        const formattedValue = this.outputFormat
          ? date.format(this.outputFormat)
          : getLocalIsoString(date);

        this.propagateChange(formattedValue);
      });
  }

  ngAfterViewInit() {
    this.hostControl = this.inj.get(NgControl);
    const control = this.hostControl.control;

    const originalMarkAsDirty = control.markAsDirty.bind(control);

    control.markAsDirty = () => {
      originalMarkAsDirty();

      if (control.dirty) {
        this.markControlsAsDirty([this.ts, this.fullTime]);
      }
    };
  }

  setDisabledState(isDisabled: boolean) {
    this.qtDisabled = isDisabled;
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(
    value:
      | string
      | Date
      | number
      | { a?: any; val?: any; value?: any; date?: any }
  ): void {
    const rawValue = this.extractRawValue(value);

    if (
      rawValue === null ||
      typeof rawValue === 'undefined' ||
      rawValue === ''
    ) {
      this.lastValid = null;
      this.ts.reset(undefined, { emitEvent: false });
      this.fullTime.reset({ hour: '00', minutes: '00' }, { emitEvent: false });
      this.cdr.markForCheck();
      return;
    }

    const date = this.toMoment(rawValue);
    if (!date || !date.isValid()) {
      this.lastValid = null;
      this.ts.reset(undefined, { emitEvent: false });
      this.fullTime.reset({ hour: '00', minutes: '00' }, { emitEvent: false });
      this.cdr.markForCheck();
      return;
    }

    this.lastValid = date.clone();

    this.ts.setValue(date.toDate(), { emitEvent: false });
    const hour =
      date.get('hours') < 10 ? `0${date.get('hours')}` : date.get('hours');
    const minutes =
      date.get('minutes') < 10
        ? `0${date.get('minutes')}`
        : date.get('minutes');
    this.fullTime.setValue(
      { hour: hour.toString(), minutes: minutes.toString() },
      { emitEvent: false }
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private toMoment(value: any): moment.Moment | null {
    if (moment.isMoment(value)) {
      return value.clone();
    }
    if (value instanceof Date) {
      return moment(value);
    }
    if (!value && value !== 0) {
      return null;
    }
    if (typeof value === 'string') {
      if (value.length === 13 && /^\d+$/.test(value)) {
        return moment(parseInt(value, 10));
      }
      if (this.outputFormat) {
        const parsed = moment(value, this.outputFormat);
        if (parsed.isValid()) {
          return parsed;
        }
      }
      return moment(value);
    }

    return moment(value as any);
  }

  private extractRawValue(value: any): any {
    if (!value || moment.isMoment(value)) {
      return value;
    }

    if (typeof value === 'object') {
      const v: any = value;
      return v.a ?? v.val ?? v.value ?? v.date ?? v._d ?? value;
    }

    return value;
  }

  private markControlsAsDirty(controls: AbstractControl[]) {
    controls.forEach(control => {
      if (control) {
        control.markAsDirty();
      }
    });
  }
}
